import { useEffect, useState, ReactNode, useMemo } from "react";
import "./review.less";
import ReviewAICheck from "./reviewGenerator/ReviewAICheck";
import ReviewKeywordsSelect from "./reviewGenerator/ReviewKeywordsSelect";
import { Kwd, ReviewPageProps, ReviewStep, UserReviewSubmitData } from "./Review.types";
import { useRequest } from "standalone/hooks/useRequest";
import normalizer from "services/store/middlewares/api/json-api-normalizer/normalizer";
import FormattedMessage from "components/common/FormattedMessage";
import { Space, Alert, ConfigProvider } from "antd";
import ReviewMainPage from "./ReviewMainPage";
import ReviewForm from "./ReviewForm";
import ReviewButtons from "./ReviewButtons";
import ReviewThankYou from "./ReviewThankYou";
import Spinner from "components/common/Spinner";

const API_URL = import.meta.env.VITE_API_URL;

const ReviewPage = ({ reviewId, reviewData }: ReviewPageProps) => {

    const [step, setStep] = useState<ReviewStep>("loading");
    const [submitData, setSubmitData] = useState<UserReviewSubmitData>({
        stars: null,
        usesAI: null,
        selectedKeywords: [],
        reviewDraft: null,
        review: null
    });
    const [error, setError] = useState<ReactNode | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const generateCall = useRequest({});

    const usesAI = reviewData?.customization?.ai_enabled ?? false;
    const hasButtons = Boolean(reviewData?.google_button_link) || Boolean(reviewData?.booksy_button_link);
    const allKeywords = reviewData?.selected_opinion_keywords || [];
    const hasKeywords = allKeywords?.length > 0;

    useEffect(() => {
        console.log('reviewData', reviewData);
    }, [reviewData]);

    useEffect(() => {
        console.log('submitData', submitData);
    }, [submitData]);

    useEffect(() => {
        console.log('step', step);
    }, [step]);

    useEffect(() => {

        if (!reviewData) return;
        if (reviewData?.valid_until && new Date(reviewData?.valid_until) < new Date()) {
            setError(
              <Alert
                message={
                  <FormattedMessage
                    id="standalone.reviews.errors.expired.message"
                    defaultMessage="Link Expired"
                    description="Reviews: Tytuł błędu gdy link wygasł."
                  />
                }
                description={
                  <FormattedMessage
                    id="standalone.reviews.errors.expired.description"
                    defaultMessage="This link has expired and is no longer available."
                    description="Reviews: Opis błędu gdy link wygasł."
                  />
                }
                type="error"
                showIcon
              />
            );
        } else {
            setStep("main");
        }

    }, [reviewData]);

    const handleGenerate = () => {
        setIsLoading(true);
        generateCall.call(() => {
            return fetch(`${API_URL}v1/reviews/${reviewData?.id}/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    keywords: submitData.selectedKeywords
                })
            })
        }).then((res) => {
            if (!res.ok) {
                throw new Error('Failed to fetch review');
            }
            return res.json();
        }).then((res) => {
            console.log('res', res);
            return normalizer(res)
        })
            .then((res) => {
                const review = res?.reviews?.review;
                setSubmitData(prev => ({ ...prev, review: review }));
                setStep("editorWithAI");
            }).catch((err) => {
                console.error("Error generating review:", err);
            }).finally(() => {
                setIsLoading(false);
            });

    }

    // Step navigation functions that set both data and state
    const setStars = (stars: number) => {
        setSubmitData(prev => ({ ...prev, stars }));

        if (stars === 5) {
            if (reviewData?.customization?.ai_enabled) {
                setStep("aiCheck");
            } else {
                setStep("editorWithoutAI");
            }
        } else {
            setStep("editorFeedback");
        }
    };

    const setUseAI = (useAI: boolean | null) => {
        setSubmitData(prev => ({ ...prev, usesAI: useAI }));
        if (useAI === null) {
            setStep("main");
        } else if (useAI && hasKeywords) {
            setStep("keywords");
        } else if (useAI) {
            setStep("editorWithAI");
        } else {
            setStep("editorWithoutAI");
        }
    };

    const handleKeywordToggle = (keyword: Kwd) => {
        setSubmitData(prev => {
            const idx = prev.selectedKeywords.findIndex(k => k === keyword);
            if (idx === -1) {
                return { ...prev, selectedKeywords: [...prev.selectedKeywords, keyword] };
            } else {
                return { ...prev, selectedKeywords: prev.selectedKeywords.filter(k => k !== keyword) };
            }
        });
    };


    const getContent = () => {

        if (error) return error;

        switch (step) {
            case "loading":
                return <Spinner />;
            
            case "main":
                return <ReviewMainPage 
                    reviewData={reviewData}
                    onStarsChange={setStars}
                />;
            
            case "aiCheck":
                return <ReviewAICheck 
                    setStars={setStars} 
                    setUseAI={setUseAI} 
                    onBack={() => {
                        setStep("main");
                        setSubmitData(prev => ({ ...prev, stars: null }));
                    }}
                />;
            
            case "keywords":
                return (
                    <ReviewKeywordsSelect
                        selectedKeywords={submitData.selectedKeywords}
                        allKeywords={allKeywords}
                        isLoading={isLoading}
                        onGenerate={handleGenerate}
                        onBack={() => {
                            setStep("aiCheck");
                            setSubmitData(prev => ({ ...prev, usesAI: null }));
                        }}
                        onKeywordToggle={handleKeywordToggle}
                    />
                );

            case "editorFeedback":
                return (
                    <ReviewForm
                    reviewData={reviewData}
                    submitData={submitData}
                        message={(
                            <FormattedMessage
                                id="standalone.reviews.feedbackFormBadRatingMessage"
                                defaultMessage="We want our Clients to be 100% satisfied. Let us know why you had a bad experience with our company. This will help us improve our services. Leave your email for contact!" 
                                description="Reviews: Wiadomość po złej ocenie"
                            />
                        )}
                        onBack={() => {
                            setStep("main");
                            setSubmitData(prev => ({ ...prev, stars: null }));
                        }}
                        onSubmit={(v) => {
                            setStep("thankYouWithoutButtons");
                            setSubmitData(prev => ({ ...prev, ...v }));
                        }}
                    />
                );

            case "editorWithAI":
                return <ReviewForm
                reviewData={reviewData}
                submitData={submitData}
                    message={(
                        <FormattedMessage
                                id="standalone.reviews.feedbackFormWithAi"
                                defaultMessage="Help us improve our services. Leave your email or phone number for contact!"
                                description="Reviews: Wiadomość po dobrej ocenie - z AI"
                            />
                    )}
                    showButtons={true}
                    onBack={() => {
                        if (usesAI) {
                            setStep("keywords");
                        } else {
                            setStep("main");
                        }
                    }}
                    onSubmit={(v) => {
                        setSubmitData(prev => ({ ...prev, ...v }));
                    }}
                />;
            
            case "editorWithoutAI":
                return <ReviewForm 
                reviewData={reviewData}
                submitData={submitData}
                message={(
                    <FormattedMessage
                    id="standalone.reviews.feedbackFormWithAi"
                    defaultMessage="Help us improve our services. Leave your email or phone number for contact!"
                    description="Reviews: Wiadomość po dobrej ocenie - bez AI"
                />
                )}
                showButtons={true}
                onBack={() => {
                    if (usesAI) {
                        setStep("aiCheck");
                    } else {
                        setStep("main");
                    }
                }}
                onSubmit={(v) => {
                    setSubmitData(prev => ({ ...prev, ...v }));
                }}
                />;
            
            case "thankYouWithButtons":
                return (
                   <ReviewButtons
                        reviewData={reviewData}
                        submitData={submitData}
                   />
                );
            
            case "thankYouWithoutButtons":
                return (
                    <ReviewThankYou />
                );
            
            default:
                return <ReviewMainPage 
                    reviewData={reviewData}
                    onStarsChange={setStars}
                />;
        }

    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: reviewData?.customization?.colors?.primary || "#3b7ad9"

                },
                components: {
                    Rate: {
                        starColor: reviewData?.customization?.colors?.stars || "#fadb14",
                    }
                }
            }}
        >
            <Space direction="vertical">
                {getContent()}
            </Space>
        </ConfigProvider>
    )

}

export default ReviewPage;